import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { authClient } from "../api/authClient";

const AuthContext = createContext(null);

const mapResultToUser = (result) => {
  const data = result?.raw;
  if (!data || (!data.user && !data.session)) return null;
  return {
    siteUserGuid: data.user?.siteUserGuid || data.session?.siteUserGuid || null,
    email: data.user?.emailAddress || null,
    userName: data.user?.userName || data.user?.emailAddress || null,
    sessionId: data.session?.sessionId || null,
  };
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [matrix, setMatrix] = useState([]); 
  const [isInitialising, setIsInitialising] = useState(true);

  const initAuth = useCallback(async () => {
    try {
      // 1. AUTH INITIALIZES FIRST (Identity Handshake)
      const result = await authClient.getSession();
      
      if (result?.ok) {
        setUser(mapResultToUser(result));
        
        // 2. SECURITY LAYER ACTIVATES (Derive Matrix from Identity)
        const storedMatrix = authClient.getMatrix();
        if (storedMatrix) setMatrix(storedMatrix);
      }
    } catch (err) {
      console.error("Architectural Boot Failure:", err);
    } finally {
      // 3. UI RENDERS ONLY AFTER BOTH ARE RESOLVED
      setIsInitialising(false);
    }
  }, []);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  const login = useCallback(async (credentials) => {
    const result = await authClient.login(credentials);
    if (result.ok) {
      // Atomic update: Identity, then Security
      setUser(mapResultToUser(result));
      setMatrix(result.matrix || []);
    }
    return result;
  }, []);

  const logout = useCallback(async () => {
    await authClient.signOut();
    setUser(null);
    setMatrix([]);
  }, []);

  const value = useMemo(() => ({
    user,
    matrix,
    isInitialising,
    isAuthenticated: !!user,
    login,
    logout
  }), [user, matrix, isInitialising, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = { children: PropTypes.node.isRequired };
export const useAuth = () => useContext(AuthContext);